import { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from 'react'

import Box from '@mui/material/Box'

const PARIS_COORDINATE = { lat: 48.864716, lng: 2.349014 }

export type GoogleMapHandle = {
  addMarker: (markerId: string, marker: google.maps.Marker) => void
  createMarker: (lat: number, lng: number, icon: google.maps.Icon, zIndex?: number) => google.maps.Marker
  removeMarker: (markerId: string) => void
}

export type GoogleMapProps = {
  onLoad?: (map: google.maps.Map) => void
}

export const GoogleMap = forwardRef(function GoogleMap(props: GoogleMapProps, ref: Ref<GoogleMapHandle>) {
  const { onLoad } = props

  useImperativeHandle(ref, () => ({
    addMarker,
    createMarker,
    removeMarker,
  }))

  const mapContainerRef = useRef<HTMLDivElement>(null)

  const bounds = useRef<google.maps.LatLngBounds | null>(null)
  const markers = useRef<Map<string, google.maps.Marker>>(new Map())
  // const lines = useRef<Map<string, google.maps.Polyline>>(new Map())

  const [map, setMap] = useState<google.maps.Map | null>(null)

  useEffect(() => {
    if (!mapContainerRef.current) return
    bounds.current = new google.maps.LatLngBounds()
    const map = new google.maps.Map(mapContainerRef.current, {
      // styles: mapStyles,
      center: PARIS_COORDINATE,
      zoom: 12,
      fullscreenControl: false,
      mapTypeControl: false,
      streetViewControl: false,
    })
    setMap(map)
    onLoad?.(map)
  }, [onLoad])

  const _addMarker = (markerId: string, marker: google.maps.Marker) => {
    markers.current.set(markerId, marker)
  }

  const _getMarker = (markerId: string) => {
    return markers.current.get(markerId)
  }

  const _removeMarker = (markerId: string) => {
    if (markers.current.has(markerId)) markers.current.delete(markerId)
  }

  const _fitToMarker = () => {
    if (!map) return

    const newBounds = new google.maps.LatLngBounds()
    markers.current.forEach((marker) => {
      const position = marker.getPosition()
      if (position) newBounds.extend(position)
    })
    if (markers.current.size > 1) {
      map.fitBounds(newBounds)
      map.panToBounds(newBounds)
    }
    bounds.current = newBounds
  }

  const createMarker = (lat: number, lng: number, icon: google.maps.Icon, zIndex: number = 3) => {
    const position = { lat, lng }
    bounds.current?.extend(position)
    return new google.maps.Marker({ icon, position, zIndex })
  }

  const addMarker = (markerId: string, marker: google.maps.Marker) => {
    if (!map) return
    console.log(map instanceof google.maps.Map)
    console.log(marker)
    marker.setMap(map)
    _addMarker(markerId, marker)
    _fitToMarker()
  }

  const removeMarker = (markerId: string) => {
    if (!markers.current.has(markerId)) return

    const marker = _getMarker(markerId)
    marker?.setMap(null)
    _removeMarker(markerId)
  }

  return <Box ref={mapContainerRef} height="100%" />
})

export default GoogleMap
